import { Link } from "react-router-dom";
import Seo, { orgSchema, breadcrumbSchema } from "@/components/Seo";
import Crumbs from "@/components/Crumbs";
import HelpCta from "@/components/HelpCta";
import { Target, Network, Handshake, ArrowUpRight } from "lucide-react";

const STATS = [
  { value: "+88", label: "Centros de rehabilitación" },
  { value: "+55", label: "Profesionales especializados" },
  { value: "16", label: "Regiones de Chile" },
];

export default function Nosotros() {
  const crumbs = [{ name: "Quiénes Somos", path: "/nosotros" }];
  const schemas = [
    orgSchema,
    breadcrumbSchema([{ name: "Inicio", path: "/" }, ...crumbs]),
  ];

  return (
    <>
      <Seo
        title="Quiénes Somos - pasta-base.cl | Red SinAdicciones.org"
        description="pasta-base.cl es un proyecto informativo dedicado exclusivamente a la pasta base, parte de la red SinAdicciones.org, la plataforma más completa de Chile para tratamiento de adicciones."
        path="/nosotros"
        schema={schemas}
      />

      <div className="mx-auto max-w-4xl px-5 py-16 lg:px-8">
        <Crumbs items={crumbs} />
        <header>
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-400">
            Quiénes somos
          </span>
          <h1 className="mt-3 text-4xl font-extrabold tracking-tight text-slate-50 sm:text-5xl">
            Información verificada para salvar vidas
          </h1>
          <p className="mt-5 text-lg leading-relaxed text-slate-300">
            <strong className="font-semibold text-slate-50">pasta-base.cl</strong>{" "}
            es un proyecto informativo dedicado exclusivamente a la pasta base,
            su composición, efectos, tratamientos y acceso a ayuda profesional
            real. Somos parte de la red{" "}
            <a
              href="https://sinadicciones.org"
              target="_blank"
              rel="noopener noreferrer"
              className="text-emerald-400 hover:underline"
            >
              SinAdicciones.org
            </a>
            , la plataforma más completa de Chile para encontrar centros de
            rehabilitación, profesionales y tratamiento de adicciones.
          </p>
        </header>

        {/* Misión */}
        <section className="mt-12 rounded-3xl border border-emerald-500/20 bg-gradient-to-br from-emerald-500/5 to-slate-900 p-8">
          <div className="flex items-start gap-4">
            <div className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-emerald-500/10 ring-1 ring-emerald-500/30">
              <Target className="h-5 w-5 text-emerald-300" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-slate-50">Nuestra misión</h2>
              <p className="mt-3 text-base leading-relaxed text-slate-200">
                Informar, concientizar y conectar a quienes sufren adicción a la
                pasta base —o cuyos familiares la sufren— con ayuda profesional
                real. Creemos que la información clara reduce el estigma y
                abre puertas. Cada persona que llega a este sitio y encuentra
                los recursos que necesita, es una vida que se recupera.
              </p>
            </div>
          </div>
        </section>

        {/* Principios */}
        <section className="mt-10 grid gap-4 md:grid-cols-2">
          <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6">
            <Handshake className="h-5 w-5 text-cyan-300" />
            <h3 className="mt-4 text-lg font-bold text-slate-50">Información verificada</h3>
            <p className="mt-2 text-sm leading-relaxed text-slate-300">
              Todo nuestro contenido se basa en datos de SENDA, Ministerio de
              Salud de Chile y literatura clínica revisada por pares. Sin
              sensacionalismo, sin promoción de tratamientos milagrosos.
            </p>
          </div>
          <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6">
            <Network className="h-5 w-5 text-emerald-300" />
            <h3 className="mt-4 text-lg font-bold text-slate-50">Red de ayuda real</h3>
            <p className="mt-2 text-sm leading-relaxed text-slate-300">
              Derivamos a SinAdicciones.org porque agrupa centros y
              profesionales verificados en las 16 regiones de Chile. No
              vendemos tratamientos ni recibimos comisiones por derivación.
            </p>
          </div>
        </section>

        {/* Red stats */}
        <section className="mt-10 rounded-3xl border border-slate-800 bg-slate-900/50 p-8">
          <h2 className="text-2xl font-bold text-slate-50">La red SinAdicciones.org</h2>
          <p className="mt-2 text-sm text-slate-300">
            La plataforma más completa de Chile para encontrar ayuda profesional
            contra las adicciones.
          </p>
          <div className="mt-6 grid gap-4 sm:grid-cols-3">
            {STATS.map((s) => (
              <div
                key={s.label}
                className="rounded-2xl border border-slate-800 bg-slate-950/60 p-5"
              >
                <div className="bg-gradient-to-br from-emerald-300 to-cyan-300 bg-clip-text text-4xl font-extrabold text-transparent">
                  {s.value}
                </div>
                <p className="mt-2 text-sm text-slate-300">{s.label}</p>
              </div>
            ))}
          </div>
          <div className="mt-6">
            <a
              href="https://sinadicciones.org"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nosotros-sinadicciones-link"
              className="btn-press inline-flex items-center gap-2 rounded-full bg-emerald-500 px-5 py-2.5 text-sm font-semibold text-slate-950 hover:bg-emerald-400"
            >
              Ir a SinAdicciones.org
              <ArrowUpRight className="h-4 w-4" />
            </a>
          </div>
        </section>

        {/* Internal links */}
        <section className="mt-10">
          <h2 className="text-2xl font-bold text-slate-50">Explora el sitio</h2>
          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            <Link
              to="/que-es-pasta-base"
              className="group flex items-center justify-between rounded-2xl border border-slate-800 bg-slate-900/50 px-5 py-4 transition hover:border-emerald-500/40"
            >
              <span className="font-semibold text-slate-100 group-hover:text-emerald-300">
                ¿Qué es la Pasta Base?
              </span>
              <ArrowUpRight className="h-4 w-4 text-emerald-400" />
            </Link>
            <Link
              to="/efectos"
              className="group flex items-center justify-between rounded-2xl border border-slate-800 bg-slate-900/50 px-5 py-4 transition hover:border-emerald-500/40"
            >
              <span className="font-semibold text-slate-100 group-hover:text-emerald-300">
                Efectos
              </span>
              <ArrowUpRight className="h-4 w-4 text-emerald-400" />
            </Link>
            <Link
              to="/test-dependencia"
              className="group flex items-center justify-between rounded-2xl border border-slate-800 bg-slate-900/50 px-5 py-4 transition hover:border-emerald-500/40"
            >
              <span className="font-semibold text-slate-100 group-hover:text-emerald-300">
                Test de Dependencia
              </span>
              <ArrowUpRight className="h-4 w-4 text-emerald-400" />
            </Link>
            <Link
              to="/tratamientos"
              className="group flex items-center justify-between rounded-2xl border border-slate-800 bg-slate-900/50 px-5 py-4 transition hover:border-emerald-500/40"
            >
              <span className="font-semibold text-slate-100 group-hover:text-emerald-300">
                Tratamientos
              </span>
              <ArrowUpRight className="h-4 w-4 text-emerald-400" />
            </Link>
          </div>
        </section>
      </div>

      <section className="border-t border-slate-800 bg-slate-950">
        <div className="mx-auto max-w-7xl px-5 py-16 lg:px-8">
          <HelpCta testidPrefix="nosotros" />
        </div>
      </section>
    </>
  );
}
